/**
 * ProviderShell — the frame every provider screen hangs in (EP-004P Phase 1).
 *
 * == The refusal states are the main feature ==============================
 *
 * On the day this ships, every provider will hit a refusal. No role assignment
 * has been ratified, no provider is assigned to a facility, and no care
 * assignment exists — so the honest first experience of this portal is being
 * turned away.
 *
 * A generic "Access denied" would send ten people to ask why, and the answer
 * would be different for each of them. So each refusal names which of the four
 * gates the caller is behind, in words that tell them who can open it. That is
 * the difference between a support ticket and a person knowing what to ask for.
 *
 * These states are not error handling bolted on afterwards. They are the
 * screens most likely to be seen, and they are written first.
 *
 * == Frontend visibility is never authorization ==========================
 *
 * Nothing here decides what the caller may see. The shell asks the server what
 * it is allowed to open and renders that answer. A facility absent from the
 * switcher is absent because the server did not name it, and typing its id
 * into the URL produces a 403 from the same gate rather than a page.
 */

import React, { useCallback, useEffect, useState } from 'react';
import WorkspaceMenu from '../../components/WorkspaceMenu';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import Today from './Today';
import Schedule from './Schedule';
import Manifest, { ManifestSaveOutcome } from './Manifest';
import FeedbackDrawer, { FeedbackButton } from '../../components/feedback/FeedbackDrawer';
import WorkflowPulse from '../../components/feedback/WorkflowPulse';
import { PromptSource } from '../../services/feedbackApi';
import { providerApi, DaySheet, FacilityScope, ProviderFacility, RosterLens, RosterOrder, DEFAULT_LENS, isLens, ManifestPatch } from './providerApi';
import { StateBlock as TideState } from '../../components/tide';
// The shared workspace bridge first (Tide phase 2), then this portal's own sheet.
import '../../styles/tide/workspace.css';
import './provider.css';

/**
 * The estate, for a caller whose access is not limited to particular
 * buildings: the ones with a visit coming up first, anything by name on
 * request, and the total so the page reads as a window rather than the
 * whole list. The search runs on the server; this never holds 3,688 rows.
 */
function FacilityPicker({
  facilities, total, query, busy, onSearch, onOpen,
}: {
  facilities: ProviderFacility[]; total: number; query: string; busy: boolean;
  onSearch: (q: string) => void; onOpen: (id: string) => void;
}) {
  // Seeded once from the server's query, never re-synced by an effect: the
  // shell reloads scope on mount and on every search, and an effect syncing
  // this would clear what the caller is halfway through typing. The parent
  // keys this component on the query, so a new result remounts it instead.
  const [draft, setDraft] = useState(query);
  const shown = facilities.length;
  return (
    <section className="pv-card pv-picker" aria-label="Choose a facility">
      <div className="pv-card-head">
        <div>
          <h2>Choose a facility</h2>
          <p className="why">
            Your access is not limited to particular buildings, so this is all {total.toLocaleString()} of them.
            {query ? '' : ' The ones with a visit coming up are first.'}
          </p>
        </div>
      </div>
      <form
        className="pv-search"
        role="search"
        onSubmit={(e) => { e.preventDefault(); onSearch(draft.trim()); }}
      >
        <input
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Search by facility or city"
          aria-label="Search by facility or city"
        />
        <button type="submit" disabled={busy}>Search</button>
        {query && <button type="button" className="pv-clear" onClick={() => { setDraft(''); onSearch(''); }}>Clear</button>}
      </form>
      {shown === 0 ? (
        <TideState kind="empty" reason={query ? 'filtered' : 'none'} message={query ? `No facility matches “${query}”.` : 'No facilities are recorded yet.'} />
      ) : (
        <ul className="pv-facilities">
          {facilities.map((f) => (
            <li key={f.id}>
              <div className="who">
                <strong>{f.name}</strong>
                <span>{[f.city, f.state].filter(Boolean).join(', ') || 'Location not recorded'}{f.region ? ` · ${f.region}` : ''}</span>
              </div>
              <button type="button" className="pv-open" onClick={() => onOpen(f.id)}>Open</button>
            </li>
          ))}
        </ul>
      )}
      {shown > 0 && shown < total && (
        <p className="pv-more">
          Showing {shown} of {total.toLocaleString()}. Search to narrow it.
        </p>
      )}
    </section>
  );
}

type Tab = 'today' | 'manifest' | 'schedule';

const TABS: readonly Tab[] = ['today', 'manifest', 'schedule'];
const TAB_LABELS: Record<Tab, string> = {
  today: 'Today',
  manifest: 'Manifest',
  schedule: 'Schedule',
};

function isTab(value: string | null): value is Tab {
  return value !== null && (TABS as readonly string[]).includes(value);
}

/**
 * The facility stays in the path because it is the provider's operating scope.
 * The view stays in the query string because it is a presentation of that same
 * day sheet rather than a different resource. Today is canonical without a
 * query so existing provider links remain valid.
 *
 * The manifest's lens rides in the query too (`?lens=enrolled`), so a refresh,
 * Back, a shared link and a feedback record all name the same list. Census is
 * the default and is omitted, so existing `?view=manifest` links are unchanged.
 * A lens is a semantic word, never an identifier: nothing about a resident is
 * ever in a URL.
 */
function providerPath(facilityId: string, tab: Tab, lens: RosterLens = DEFAULT_LENS): string {
  const base = `/provider/${facilityId}`;
  if (tab === 'today') return base;
  const withLens = tab === 'manifest' && lens !== DEFAULT_LENS ? `&lens=${lens}` : '';
  return `${base}?view=${tab}${withLens}`;
}

/**
 * Why the caller cannot see something, in the terms the platform actually
 * uses. `code` comes from the authorization decision; the copy is chosen per
 * code so nobody has to guess which gate they are behind.
 */
interface Refusal {
  title: string;
  body: string;
  detail?: string;
  /** The server was not reached, failed, or answered without the data: danger tone, Try again. Absent = a refusal at a gate. */
  failure?: boolean;
  status?: number;
}

const REFUSALS: Record<string, Refusal> = {
  DENY_NO_CAPABILITY: {
    title: 'Your access has not been activated yet',
    body:
      'Your role exists but has not been ratified, so it grants nothing yet. '
      + 'Ratification is a deliberate human step — someone has to approve your role '
      + 'before it takes effect. Ask an administrator to ratify your assignment.',
    detail: 'Decision: DENY_NO_CAPABILITY',
  },
  DENY_SCOPE: {
    title: 'This facility is not one of yours',
    body:
      'Your access is real, but it does not cover this building. If you have started '
      + 'covering it, ask for a facility assignment rather than a new role.',
    detail: 'Decision: DENY_SCOPE',
  },
};

const GENERIC_REFUSAL: Refusal = {
  title: 'You do not have access to this',
  body: 'The server declined this request. If you believe that is wrong, ask an administrator to check your role assignment.',
};

/**
 * The decision the server sent, looked up by its bare name. Authorization V2
 * codes arrive prefixed (`AUTHZ_DENY_NO_CAPABILITY`); the D-04 gate's does
 * not. Before this every V2 refusal fell through to the generic copy, and
 * nobody was told which gate they were behind.
 */
function refusalFor(code: string | undefined | null, status?: number, message?: string): Refusal {
  const bare = String(code || '').replace(/^AUTHZ_/, '');
  if (REFUSALS[bare]) return REFUSALS[bare];
  // Status before the generic copy (2026-10 fix): a 500 or a dropped connection
  // used to read "You do not have access to this", a permissions problem that
  // was not one.
  if (status === undefined && !code) return UNREACHED;
  if (typeof status === 'number' && status >= 500) {
    return { title: 'Server error', body: 'The server failed while loading this. This is not a permissions problem.', failure: true, status };
  }
  if (status === 401 || status === 403) return GENERIC_REFUSAL;
  return { title: 'Not loaded', body: message || 'The server answered without what this screen needs.', failure: true, status };
}


/** A read that never reached the server (no status and no code), or threw before it could. */
const UNREACHED: Refusal = { title: 'Server not reached', body: 'Could not reach the server, so nothing here is current.', failure: true };

/**
 * A refusal or a failure: the shared Tide StateBlock with this portal's own
 * heading, words and detail. A failure is danger with Try again; a refusal (or
 * no building assigned) is the withheld tone. Never an empty building.
 */
function StateBlock({ refusal, retry }: { refusal: Refusal; retry?: () => void }) {
  return (
    <div className="pv-state">
      <TideState kind="error" heading={refusal.title} headingLevel={2} message={refusal.body} detail={refusal.detail}
        status={refusal.status} tone={refusal.failure ? 'danger' : 'withheld'} onRetry={retry} />
    </div>
  );
}

/*
 * `role="status"` is what makes the loading state discoverable to assistive
 * technology, and `aria-busy` is what would have silenced it: a busy live
 * region is one AT may hold announcements on until it stops being busy, and
 * this one never does — it unmounts. The sr-only text says what the shimmer
 * shows. Where two skeletons stand together only the first speaks, so the
 * provider hears one announcement rather than one per placeholder.
 */
function Skeleton({ announce = true }: { announce?: boolean }) {
  return (
    <section className="pv-card">
      <TideState kind="loading" shape="table" label="Loading provider data…" busy={false} srOnlyLabel announce={announce} />
    </section>
  );
}

const ROSTER_PAGE = 50;

export default function ProviderShell() {
  const { facilityId } = useParams<{ facilityId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawView = searchParams.get('view');
  const tab: Tab = isTab(rawView) ? rawView : 'today';
  const rawLens = searchParams.get('lens');
  const lens: RosterLens = isLens(rawLens) ? rawLens : DEFAULT_LENS;

  const [scope, setScope] = useState<FacilityScope | null>(null);
  const [scopeRefusal, setScopeRefusal] = useState<Refusal | null>(null);
  const [scopeLoading, setScopeLoading] = useState(true);

  const [sheet, setSheet] = useState<DaySheet | null>(null);
  const [sheetRefusal, setSheetRefusal] = useState<Refusal | null>(null);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [offset, setOffset] = useState(0);

  /*
   * The feedback door. One drawer for the whole shell, opened from the
   * utility area or by answering "had friction" to the after-workflow
   * question; the record says which. It is available on the refusal screens
   * too — a provider who was turned away has exactly the friction this
   * exists to hear.
   */
  const [fbOpen, setFbOpen] = useState(false);
  const [fbSource, setFbSource] = useState<PromptSource>('DRAWER');
  const openFeedback = useCallback((source: PromptSource) => { setFbSource(source); setFbOpen(true); }, []);
  const closeFeedback = useCallback(() => setFbOpen(false), []);

  const loadScope = useCallback(async (q?: string) => {
    setScopeLoading(true);
    setScopeRefusal(null);
    try {
      const res = await providerApi.getScope(q);
      if (res.success && res.data) setScope(res.data);
      else setScopeRefusal(refusalFor(res.code, res.status, res.message));
    } catch {
      setScopeRefusal(UNREACHED);
    } finally {
      setScopeLoading(false);
    }
  }, []);

  useEffect(() => { loadScope(); }, [loadScope]);

  /*
   * An unknown view is not a fourth workflow. Normalize it to Today so copied
   * or hand-edited links always land in a state the shell can explain.
   */
  useEffect(() => {
    if (!facilityId || rawView === null || isTab(rawView)) return;
    navigate(providerPath(facilityId, 'today'), { replace: true });
  }, [facilityId, rawView, navigate]);

  // An unknown lens is Census, and the URL is corrected in place so what the
  // address bar says and what the list shows are the same thing.
  useEffect(() => {
    if (!facilityId || rawLens === null || isLens(rawLens)) return;
    navigate(providerPath(facilityId, tab, DEFAULT_LENS), { replace: true });
  }, [facilityId, rawLens, tab, navigate]);

  /*
   * Land on the first facility when the caller has one and named none. A
   * provider covering a single building should not have to choose it. If a
   * valid view was deep-linked at /provider?view=..., preserve that intent.
   *
   * Never for an enterprise caller. Their list is the first page of the
   * whole estate, so "the first one" is whichever building sorts first —
   * a destination nobody chose. They get the picker instead.
   */
  useEffect(() => {
    if (!scope || facilityId || scope.enterprise) return;
    if (scope.facilities.length > 0) {
      navigate(providerPath(scope.facilities[0].id, tab, lens), { replace: true });
    }
  }, [scope, facilityId, tab, lens, navigate]);

  // The lens narrows the roster (it lives in the URL); order is how the
  // provider walks the building. Both are server-side (the roster is paged),
  // and both reset the page. Room order is the default: a corridor is not
  // alphabetical.
  const [order, setOrder] = useState<RosterOrder>('room');

  const loadSheet = useCallback(async (id: string, nextOffset: number, l: RosterLens, o: RosterOrder) => {
    setSheetLoading(true);
    setSheetRefusal(null);
    try {
      const res = await providerApi.getDaySheet(id, { limit: ROSTER_PAGE, offset: nextOffset, lens: l, order: o });
      if (res.success && res.data) {
        setSheet(res.data);
      } else {
        setSheet(null);
        setSheetRefusal(refusalFor(res.code, res.status, res.message));
      }
    } catch {
      setSheet(null);
      setSheetRefusal({ ...UNREACHED, title: 'Could not load this facility' });
    } finally {
      setSheetLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!facilityId) return;
    loadSheet(facilityId, offset, lens, order);
  }, [facilityId, offset, lens, order, loadSheet]);

  const onLens = (l: RosterLens) => { if (!facilityId) return; setOffset(0); navigate(providerPath(facilityId, 'manifest', l)); };
  const onOrder = (o: RosterOrder) => { setOffset(0); setOrder(o); };

  /**
   * Save a resident's provider-owned manifest columns, then re-read the sheet
   * so the row shows what was recorded (workforce cutover, 2026-09-19). The
   * outcome is a sentence for the row, or null when it took.
   */
  const onSaveManifest = async (patientId: string, patch: ManifestPatch): Promise<ManifestSaveOutcome> => {
    if (!facilityId) return 'No facility is open.';
    try {
      const r = await providerApi.updateManifest(facilityId, patientId, patch);
      if (!r.success) {
        if (r.code === 'FIELD_NOT_OWNED_BY_EMERGE') return 'That column is still owned by the scheduling board; it cannot be changed here yet.';
        if (r.code === 'PATIENT_NOT_AT_FACILITY') return 'This resident is no longer confirmed at this building. Reload the manifest.';
        return r.error || 'The change was not recorded.';
      }
      await loadSheet(facilityId, offset, lens, order);
      return null;
    } catch { return 'Could not reach the server. Nothing was changed.'; }
  };

  /** Check in: record the event, then re-read the sheet so the screen shows what was recorded. */
  const onCheckIn = async (contactId: string | null): Promise<boolean> => {
    if (!facilityId) return false;
    try {
      const r = await providerApi.checkIn(facilityId, contactId);
      if (!r.success) return false;
      await loadSheet(facilityId, offset, lens, order);
      return true;
    } catch { return false; }
  };

  // A new building starts on Census: the lens was a question about the
  // building the provider just left.
  const onSwitch = (id: string) => {
    setOffset(0);
    setSheet(null);
    navigate(providerPath(id, tab));
  };

  // Rendered on every screen below, including the refusals. The route is a
  // template and the view a name: never the URL, which could carry anything.
  const feedback = (
    <FeedbackDrawer
      open={fbOpen}
      onClose={closeFeedback}
      promptSource={fbSource}
      workspace="PROVIDER"
      routeTemplate={facilityId ? '/provider/:facilityId' : '/provider'}
      viewName={tab === 'manifest' ? `manifest:${lens}` : tab}
      facilityId={facilityId ?? null}
    />
  );
  const door = (
    <div className="pv-door">
      <FeedbackButton onClick={() => openFeedback('DRAWER')} />
    </div>
  );

  // ── Refusals and empty scope, before any facility chrome renders ──────

  if (scopeLoading) {
    return <div className="pv"><main className="pv-main"><Skeleton /></main></div>;
  }

  if (scopeRefusal) {
    return (
      <div className="pv">
        <main className="pv-main">
          <StateBlock refusal={scopeRefusal} retry={loadScope} />
          {door}
        </main>
        {feedback}
      </div>
    );
  }

  /*
   * Enterprise scope, and no facility chosen yet: offer the estate as a
   * searchable page rather than a sentence.
   *
   * Until 2026-09-08 this branch was a refusal telling the caller to "open a
   * facility directly by its link" — a link nobody has. So the widest access
   * in the platform produced the narrowest screen, and the operations analyst
   * who designed this workflow could not open the portal she was reviewing.
   * Breadth of access is not a reason to be handed nothing.
   */
  if (scope && scope.enterprise && !facilityId) {
    return (
      <div className="pv">
        <main className="pv-main">
          <FacilityPicker
            key={scope.query || ''}
            facilities={scope.facilities}
            total={scope.total ?? scope.facilities.length}
            query={scope.query || ''}
            busy={scopeLoading}
            onSearch={(q) => loadScope(q || undefined)}
            onOpen={(id) => navigate(providerPath(id, 'today'))}
          />
          {door}
        </main>
        {feedback}
      </div>
    );
  }

  if (scope && !scope.enterprise && scope.facilities.length === 0) {
    // Provisioned but assigned nowhere. This is a different condition from
    // being refused, and conflating the two would send someone hunting for a
    // permissions bug that does not exist.
    return (
      <div className="pv">
        <main className="pv-main">
          <StateBlock refusal={{
            title: 'You are not assigned to a facility yet',
            body:
              'Your access is active, but no building has been assigned to you. A provider '
              + 'sees a facility once someone records that you cover it — ask for a facility '
              + 'assignment, not for more permissions.',
          }}
          />
          {door}
        </main>
        {feedback}
      </div>
    );
  }

  const facilities = scope?.facilities || [];
  const current = facilities.find((f) => f.id === facilityId);

  return (
    <div className="pv">
      <WorkspaceMenu current="PROVIDER" />
      <header className="pv-bar">
        <div className="pv-bar-inner">
          <div className="pv-ident">
            <div className="pv-where">
              <span className="pv-eyebrow">eMerge · Provider</span>
              <h1>{current?.name || sheet?.facility.name || 'Select a facility'}</h1>
              {current && (current.city || current.state) && (
                <span className="pv-sub">
                  {[current.city, current.state].filter(Boolean).join(', ')}
                </span>
              )}
            </div>

            <div className="pv-aside">
              {facilities.length > 1 && (
                <div className="pv-switch">
                  <label htmlFor="pv-facility">Facility</label>
                  <select
                    id="pv-facility"
                    value={facilityId || ''}
                    onChange={(e) => onSwitch(e.target.value)}
                  >
                    {facilities.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {/* The utility area. Feedback is a door here, not a fourth tab:
                  it is not a workflow destination. */}
              <FeedbackButton onClick={() => openFeedback('DRAWER')} />
            </div>
          </div>

          {/* The workbook links live on the Manifest, where they are opened.
              A link in the shell would follow the provider onto Today and
              Schedule, where neither workbook is what they are looking at. */}
          <nav className="pv-tabs" aria-label="Provider views">
            {TABS.map((target) => (
              <Link
                key={target}
                to={facilityId ? providerPath(facilityId, target) : '/provider'}
                className={tab === target ? 'on' : ''}
                aria-current={tab === target ? 'page' : undefined}
              >
                {TAB_LABELS[target]}
              </Link>
            ))}
          </nav>
        </div>
      </header>

      <main className="pv-main">
        {/* A refused or failed day sheet can be retried in place, as the scope
            can. Without this a connection blip meant reloading the page. */}
        {sheetRefusal && (
          <StateBlock
            refusal={sheetRefusal}
            retry={facilityId ? () => loadSheet(facilityId, offset, lens, order) : undefined}
          />
        )}
        {!sheetRefusal && (sheetLoading && !sheet) && <><Skeleton /><Skeleton announce={false} /></>}
        {!sheetRefusal && sheet && tab === 'today' && (
          <Today sheet={sheet} onPage={setOffset} onCheckIn={onCheckIn} />
        )}
        {!sheetRefusal && sheet && tab === 'manifest' && (
          <Manifest sheet={sheet} onPage={setOffset} onLens={onLens} onOrder={onOrder} stockReportUrl={scope?.links?.stock_report ?? null} onSave={onSaveManifest} />
        )}
        {!sheetRefusal && sheet && tab === 'schedule' && <Schedule sheet={sheet} />}

        {/* After the work, not before it: the question appears once the
            provider has actually been in this view for a while, once per
            sitting per view, and "had friction" opens the same drawer. */}
        {!sheetRefusal && sheet && facilityId && (
          <WorkflowPulse
            storageKey={`fb-pulse:PROVIDER:${tab}`}
            onFriction={() => openFeedback('WORKFLOW_PROMPT')}
          />
        )}
      </main>
      {feedback}
    </div>
  );
}
